import { useState, useEffect } from 'react'
import { Modal, Input, Select } from '../../../shared/components'
import Button from '../../../shared/components/Button'
import { useCreateAttributeSchema, useUpdateAttributeSchema } from '../hooks/useAttributeSchemas'
import type { AttributeSchema, AttributeDataType, SolutionId } from '../types'
import { SOLUTION_IDS } from '../types'

interface AttributeSchemaModalProps {
  isOpen: boolean
  onClose: () => void
  schema?: AttributeSchema | null
}

const DATA_TYPE_OPTIONS: { title: string; value: AttributeDataType }[] = [
  { title: 'Enum (dropdown)', value: 'ENUM' },
  { title: 'Color picker', value: 'COLOR' },
  { title: 'Text', value: 'STRING' },
  { title: 'Number', value: 'NUMBER' },
]

const SOLUTION_OPTIONS = SOLUTION_IDS.map((s) => ({ title: s, value: s }))

export const AttributeSchemaModal = ({ isOpen, onClose, schema }: AttributeSchemaModalProps) => {
  const isEdit = !!schema

  const [solutionId, setSolutionId] = useState<SolutionId>('rack-and-box')
  const [tenantId, setTenantId] = useState('')
  const [key, setKey] = useState('')
  const [label, setLabel] = useState('')
  const [dataType, setDataType] = useState<AttributeDataType>('STRING')
  const [allowedValuesRaw, setAllowedValuesRaw] = useState('')
  const [isRequired, setIsRequired] = useState(true)
  const [sortOrder, setSortOrder] = useState('0')

  useEffect(() => {
    if (schema) {
      setSolutionId(schema.solutionId as SolutionId)
      setTenantId(schema.tenantId ?? '')
      setKey(schema.key)
      setLabel(schema.label)
      setDataType(schema.dataType)
      setAllowedValuesRaw(schema.allowedValues.join(', '))
      setIsRequired(schema.isRequired)
      setSortOrder(String(schema.sortOrder))
    } else {
      setSolutionId('rack-and-box')
      setTenantId('')
      setKey('')
      setLabel('')
      setDataType('STRING')
      setAllowedValuesRaw('')
      setIsRequired(true)
      setSortOrder('0')
    }
  }, [schema, isOpen])

  const createMutation = useCreateAttributeSchema()
  const updateMutation = useUpdateAttributeSchema()

  const isPending = createMutation.isPending || updateMutation.isPending

  function parseAllowedValues() {
    return allowedValuesRaw
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
  }

  async function handleSubmit() {
    const allowedValues = parseAllowedValues()

    if (isEdit && schema) {
      await updateMutation.mutateAsync({
        id: schema.id,
        data: { label, dataType, allowedValues, isRequired, sortOrder: parseInt(sortOrder) || 0 },
      })
    } else {
      await createMutation.mutateAsync({
        solutionId,
        tenantId: tenantId || null,
        key,
        label,
        dataType,
        allowedValues,
        isRequired,
        sortOrder: parseInt(sortOrder) || 0,
      } as Omit<AttributeSchema, 'id'>)
    }
    onClose()
  }

  const canSave = !!key && !!label && (dataType !== 'ENUM' || parseAllowedValues().length > 0)

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="p-6 space-y-4">
        <h2 className="text-xl font-semibold">
          {isEdit ? 'Edit Attribute Schema' : 'New Attribute Schema'}
        </h2>

        {!isEdit && (
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Solution *"
              options={SOLUTION_OPTIONS}
              value={solutionId}
              size="small"
              onChange={(v) => setSolutionId(v as SolutionId)}
            />
            <Input
              label="Merchant ID (optional)"
              type="text"
              placeholder="leave blank for solution-wide"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
            />
          </div>
        )}

        {isEdit && (
          <div className="rounded bg-muted px-3 py-2 text-sm text-muted-foreground">
            <span className="font-medium">{schema?.solutionId}</span>
            {schema?.tenantId && <span className="ml-2 text-muted-foreground">merchant: {schema.tenantId}</span>}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Key *"
            type="text"
            placeholder="e.g. size, colour"
            value={key}
            onChange={(e) => setKey(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
            disabled={isEdit}
          />
          <Input
            label="Label *"
            type="text"
            placeholder="e.g. Size, Colour"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Data Type *"
            options={DATA_TYPE_OPTIONS}
            value={dataType}
            size="small"
            onChange={(v) => setDataType(v as AttributeDataType)}
          />
          <Input
            label="Sort Order"
            type="number"
            placeholder="0"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
        </div>

        {dataType === 'ENUM' && (
          <div>
            <Input
              label="Allowed Values * (comma-separated)"
              type="text"
              placeholder="XS, S, M, L, XL, XXL"
              value={allowedValuesRaw}
              onChange={(e) => setAllowedValuesRaw(e.target.value)}
            />
            {parseAllowedValues().length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {parseAllowedValues().map((v) => (
                  <span key={v} className="px-2 py-0.5 bg-muted rounded text-xs text-foreground">
                    {v}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2">
          <input
            id="isRequired"
            type="checkbox"
            checked={isRequired}
            onChange={(e) => setIsRequired(e.target.checked)}
            className="h-4 w-4 rounded border-border"
          />
          <label htmlFor="isRequired" className="text-sm text-foreground">
            Required on product form
          </label>
        </div>

        <div className="flex justify-end gap-3 border-t pt-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={isPending || !canSave}>
            {isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Schema'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
